import * as React from 'react'
import { Button, ButtonGroup } from 'reactstrap'
import { useTranslations } from '../../store/translations/hooks'
import { FC, useMemo, useState } from 'react'
import { TK } from '../../store/translations/translationKeys'
import { minutesBetweenTimes, minutesToTimeString } from '../../shared/utils'
import { AppointmentData } from '../../models'
import { DateRangeSelectorState } from '../../components/DateRangeSelector'
import moment from 'moment'
import { useQueryParam } from '../../hooks/useQueryParam'
import { ResponsiveBar } from '@nivo/bar'
import { AutocompleteTextField } from '../../components/Forms/fields'
import { CSVLink } from 'react-csv'
import { useBreakpoints } from '../../shared/hooks/useBreakpoints'
import { serviceName } from '../../shared/appointmint/appointment'
import { useGeneralSettings } from '../../store/settings/hooks'

const getTotalDurationInHours = (a: AppointmentData[]): number => (a ?? []).reduce((t, a) => t + minutesBetweenTimes(a.start, a.end), 0) / 60

const getFirstDayOfWeek = (date: string): string => {
    const d = new Date(date)
    while (d.getDay() !== 1) d.setDate(d.getDate()-1)
    return d.toISOString().substring(0, 10)
}

const getData = (
    appointments: AppointmentData[],
    window: DateRangeSelectorState,
    metric: 'hours'|'count',
    split: 'professional'|'appointmentType'|'room'|undefined,
    granularity: 'daily'|'weekly'|'monthly',
    filterValues: string[],
    typeToName: (type: string) => string,
): Record<string, string|number>[] => {

    const filter = (a: AppointmentData) => !filterValues.length || !filterValues.some(v => ![
        a.clientName ?? '',
        a.professionalName ?? '',
        typeToName(a.type),
        a.room,
    ].includes(v))

    const getDate = granularity === 'weekly' ? (a: AppointmentData) => getFirstDayOfWeek(a.date)
        : granularity === 'monthly' ? (a: AppointmentData) => a.date.substring(0, 7)
        : (a: AppointmentData) => a.date

    const getSplitKey = split === 'professional' ? (a: AppointmentData) => a.professionalName ?? ''
        : split === 'room' ? (a: AppointmentData) => a.room
        : split === 'appointmentType' ? (a: AppointmentData) => typeToName(a.type)
        : () => 'Total'

    const slitPerKeyPerDay = appointments
        .filter(a => a.date >= window.from && a.date <= window.to && filter(a))
        .reduce<Record<string, Record<string, AppointmentData[]>>>((acc, a) => {
            const date = getDate(a)
            const key = getSplitKey(a)
            return {
                ...acc,
                [key]: {
                    ...acc[key],
                    [date]: acc[key]?.[date] ? [...acc[key]?.[date], a] : [a],
                }
            }
        }, {})

    var from = moment.utc(window.from)
    var to = moment.utc(window.to).add(1, 'day')
    const diffDays = to.diff(from, 'days')
    var allDaysBetween = [...Array(diffDays).keys()]
        .map(i => moment.utc(from).add(i, 'days').toISOString().substr(0, 10))
    var days = granularity === 'monthly' ? [...new Set(allDaysBetween.map(a => a.substring(0, 7)))]
        : granularity === 'weekly' ? [...new Set(allDaysBetween.map(getFirstDayOfWeek))]
        : allDaysBetween

    return days.map(day => Object.fromEntries([
        ['date', day], 
        ...Object.entries(slitPerKeyPerDay).map(([key, perDay]) => [
            key, 
            metric === 'count' ? (perDay[day]?.length ?? 0) : getTotalDurationInHours(perDay[day] ?? [])
        ]),
    ]))
}

export const AnalyticsHours: FC<{
    metric: 'hours' | 'count',
    appointments: AppointmentData[],
    window: DateRangeSelectorState,
}> = ({
    metric,
    appointments,
    window,
}) => {
    const t = useTranslations()
    const generalSettings = useGeneralSettings()

    const bk = useBreakpoints()
    const [split, setSplit] = useState<'professional'|'appointmentType'|'room'|undefined>('professional')
    const [granularity, setGranularity] = useQueryParam<'daily'|'weekly'|'monthly'>('granularity', 'weekly')
    const [filter, setFilter] = useState<string[]>([])

    const possibleFilterValues = useMemo<string[]>(() => [...new Set([
        ...appointments.map(a => serviceName(a.type, generalSettings)),
        ...appointments.map(a => a.room),
        ...appointments.map(a => a.professionalName ?? ''),
        ...appointments.map(a => a.clientName ?? ''),
    ])], [appointments, generalSettings])

    const formatValue = (value: string|number) => {
        if (typeof value !== 'number') return value
        switch(metric) {
            case 'hours': return minutesToTimeString(value*60)
            default: return value
        }
    }

    const detailedData = getData(appointments, window, metric, split, granularity, filter, t => serviceName(t, generalSettings))

    const totals = detailedData.reduce<Record<string, number>>((acc, curr) => ({
        ...acc,
        ...Object.fromEntries(Object.entries(curr)
            .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
            .map(([k, v]) => [k, (acc[k] ?? 0) + v])),
    }), {})
    
    const totalsData = Object.entries(totals).map(([k, v]) => ({label: k, [t(TK.value)]: v}))

    const total = totalsData.map(e => e[t(TK.value)]).reduce<number>((total, value) => total + (value as number), 0)
    
    return (<>
        <ButtonGroup style={{width: '100%', marginTop: 'var(--spacer-XS)'}}>
            <Button outline color="info" active={split === 'professional'} onClick={() => setSplit(split === 'professional' ? undefined : 'professional')}>{t(TK.byProfessional)}</Button>
            <Button outline color="info" active={split === 'room'} onClick={() => setSplit(split === 'room' ? undefined : 'room')}>{t(TK.byRoom)}</Button>
            <Button outline color="info" active={split === 'appointmentType'} onClick={() => setSplit(split === 'appointmentType' ? undefined : 'appointmentType')}>{t(TK.byAppointmentType)}</Button>
        </ButtonGroup>

        <AutocompleteTextField 
            style={{marginTop: 'var(--spacer-S)'}}
            label={t(TK.filter)}
            options={possibleFilterValues}
            multiSelect
            values={filter}
            onValuesChanged={setFilter}
        />

        <hr/>
        <h2 style={{textAlign: 'center'}}><b>{t(TK.total)}:</b> {formatValue(total)}</h2>
        <hr/>

        <div style={{height:`${150+totalsData.length*20}px`, width: '100%'}}>
            <ResponsiveBar
                data={totalsData.reverse()}
                keys={[t(TK.value)]} 
                labelFormat={formatValue}
                tooltipFormat={formatValue}
                labelSkipWidth={35}
                labelSkipHeight={12}
                indexBy="label"
                margin={{ 
                    top: 10, 
                    right: 10, 
                    bottom: 50, 
                    left: totalsData.reduce((curr, item) => curr > (item.label as string).length ? curr : (item.label as string).length, 0) * 5 + 15,
                }}
                padding={0.3}
                colors={{ scheme: 'nivo' }}
                layout="horizontal"
                axisBottom={{
                    legend: metric === 'hours' ? t(TK.hours) 
                        : metric === 'count' ? t(TK.count)
                        : '',
                    legendOffset: 36,
                    legendPosition: 'middle',
                }}
                enableGridX={true}
            />
        </div>
        
        <hr/>

        <h3>{t(TK.detailed)}</h3>

        <div style={{display: 'flex', gap: 'var(--spacer-XS)', flexDirection: bk.sm ? 'column' : 'row'}}>
            <ButtonGroup>
                <Button outline color="info" active={granularity === 'daily'} onClick={() => setGranularity('daily')}>{t(TK.byDay)}</Button>
                <Button outline color="info" active={granularity === 'weekly'} onClick={() => setGranularity('weekly')}>{t(TK.byWeek)}</Button>
                <Button outline color="info" active={granularity === 'monthly'} onClick={() => setGranularity('monthly')}>{t(TK.byMonth)}</Button>
            </ButtonGroup>
        </div>

        <div style={{height:`${10+detailedData.length*25 + 40+Object.keys(totals).length*25}px`, width: '100%'}}>
            <ResponsiveBar
                data={detailedData.reverse()}
                keys={Object.keys(totals)} 
                labelFormat={formatValue}
                tooltipFormat={formatValue}
                labelSkipWidth={35}
                labelSkipHeight={12}
                indexBy="date"
                margin={{ 
                    top: 10, 
                    right: 10, 
                    bottom: 40+Object.keys(totals).length*25, 
                    left: detailedData.reduce((curr, item) => curr > (item.date as string).length ? curr : (item.date as string).length, 0) * 5 + 15,
                }}
                padding={0.3}
                colors={{ scheme: 'nivo' }}
                layout="horizontal"
                axisBottom={{
                    legend: metric === 'hours' ? t(TK.hours) 
                        : metric === 'count' ? t(TK.count)
                        : '',
                    legendOffset: 36,
                    legendPosition: 'middle',
                }}
                enableGridX={true}
                legends={[
                    {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 0,
                        translateY: 45+Object.keys(totals).length*22,
                        itemsSpacing: 2,
                        itemWidth: 300,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                            {
                                on: 'hover',
                                style: {
                                    itemOpacity: 1
                                }
                            }
                        ]
                    }
                ]}
            />
        </div>
        
        <CSVLink 
            data={detailedData} 
            separator=';' 
            target='_blank'
            filename='analysis-export.csv'
        >
            {t(TK.exportData)}
        </CSVLink>
    </>)
}
